import React, { useEffect, useState } from 'react'
import { BiPencil, BiShow, BiTrash } from 'react-icons/bi'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import services from '@services'
import { BestPracticeCategory, BestPracticeDoc, IndexHeadquarter } from '@types'
import FlexContainer from '@components/common/FlexContainer'
import Panel from '@components/common/Panel'
import Table from '@components/common/Table'
import XSpacing from '@components/common/XSpacing'
import YSpacing from '@components/common/YSpacing'
import { Button } from '@components/common/form'
import { colors } from '@constants'
import { ButtonsBar } from '@res/styledComponents/index'
import BestPracticeCategoryModal from '../modals/BestPracticeCategoryModal'
import BestPracticeDocModal from '../modals/BestPracticeDocModal'

const { RestService, UIService } = services

interface Props {
  userHeadquarters: IndexHeadquarter[]
  currentHqId: number
  bestPracticesNavEnabled: boolean
}

const BestPracticesListGroup: React.FC<Props> = ({
  userHeadquarters,
  currentHqId,
  bestPracticesNavEnabled,
}) => {
  const [navEnabled, setNavEnabledState] = useState(bestPracticesNavEnabled)
  const [editingCat, setEditingCat] = useState<
    Partial<BestPracticeCategory> | null | false
  >(false)
  const [editingDoc, setEditingDoc] = useState<
    Partial<BestPracticeDoc> | null | false
  >(false)

  const queryClient = useQueryClient()
  const catsKey = ['bestPracticeCategories', currentHqId] as const
  const docsKey = ['bestPracticeDocs', currentHqId] as const
  const invalidate = () => {
    queryClient.invalidateQueries(docsKey)
    queryClient.invalidateQueries(catsKey)
  }

  const captainBestPracticesDocsQuery = useQuery(
    docsKey,
    async (): Promise<BestPracticeDoc[]> => {
      const bestPracticesDocs = await RestService.get(
        `/headquarters/${currentHqId}/captains/best-practices`,
      )

      return bestPracticesDocs
    },
  )

  const bestPracticesCategories = useQuery(
    catsKey,
    async (): Promise<BestPracticeCategory[]> => {
      const categories = await RestService.get(
        `/headquarters/captains/best-practices/categories`,
      )

      return categories
    },
  )

  const saveCaptainBestPracticesDoc = useMutation(
    async (doc: Partial<BestPracticeDoc>): Promise<BestPracticeDoc> => {
      const res = doc.id
        ? await RestService.put(
            `/headquarters/captains/best-practices/${doc.id}`,
            doc,
          )
        : await RestService.post(`/headquarters/captains/best-practices`, doc)

      return res
    },
    {
      onSuccess: invalidate,
      onError: () =>
        UIService.FlashMessage.displayFailureMessage(
          'Failed to save document.',
        ),
    },
  )

  const isLoadingSaveDoc = saveCaptainBestPracticesDoc.isLoading

  const deleteDocMutation = useMutation(
    async (docId: string): Promise<void> => {
      await RestService.delete(`/headquarters/captains/best-practices/${docId}`)
    },
    {
      onSuccess: invalidate,
      onError: () =>
        UIService.FlashMessage.displayFailureMessage(
          'Failed to delete document.',
        ),
    },
  )

  const saveCategoryMutation = useMutation(
    async (cat: Partial<BestPracticeCategory>) => {
      const res = cat.id
        ? await RestService.put(
            `/headquarters/captains/best-practices/categories/${cat.id}`,
            cat,
          )
        : await RestService.post(
            `/headquarters/captains/best-practices/categories`,
            cat,
          )

      return res
    },
    {
      onSuccess: invalidate,
      onError: (error: any) =>
        UIService.FlashMessage.displayFailureMessage(
          `Failed to save category. ${error.response?.data?.message ?? ''}`,
        ),
    },
  )

  const isLoadingSaveCategory = saveCategoryMutation.isLoading

  const deleteCategoryMutation = useMutation(
    async (catId: string): Promise<void> => {
      await RestService.delete(
        `/headquarters/captains/best-practices/categories/${catId}`,
      )
    },
    {
      onSuccess: invalidate,
      onError: () =>
        UIService.FlashMessage.displayFailureMessage(
          'Failed to delete category.',
        ),
    },
  )

  const setNavEnabledMutation = useMutation(
    async (enabled: boolean) => {
      const res = await RestService.put(
        `/headquarters/${currentHqId}/captains/best-practices-nav`,
        { enabled },
      )

      return res
    },
    {
      onError: () =>
        UIService.FlashMessage.displayFailureMessage(
          'Failed to update Best Practices nav.',
        ),
    },
  )

  const docs = captainBestPracticesDocsQuery.data ?? []
  const categories = bestPracticesCategories.data ?? []

  useEffect(() => {
    setNavEnabledState(bestPracticesNavEnabled)
  }, [bestPracticesNavEnabled])

  const onToggleNav = async () => {
    const confirmed = await UIService?.ConfirmationModal.show({
      text: navEnabled
        ? 'Are you sure you want to disable Best Practices?'
        : 'Are you sure you want to enable Best Practices?',
    })
    if (!confirmed) {
      return
    }
    const next = !navEnabled
    setNavEnabledState(next)
    setNavEnabledMutation.mutate(next)
  }

  const onSaveCategory = (cat: Partial<BestPracticeCategory>) => {
    saveCategoryMutation.mutate(cat, { onSuccess: () => setEditingCat(false) })
  }

  const onDeleteCategory = async (id: string) => {
    const confirmed = await UIService?.ConfirmationModal.show({
      text: 'Are you sure you want to delete this category?',
    })
    if (confirmed) {
      deleteCategoryMutation.mutate(id)
    }
  }

  const onSaveDoc = (doc: Partial<BestPracticeDoc>) => {
    saveCaptainBestPracticesDoc.mutate(doc, {
      onSuccess: () => setEditingDoc(false),
    })
  }

  const onDeleteDoc = async (docId: string) => {
    const confirmed = await UIService?.ConfirmationModal.show({
      text: 'Are you sure you want to delete this document?',
    })
    if (confirmed) {
      deleteDocMutation.mutate(docId)
    }
  }

  const categoryName = (catId: string) =>
    categories.find((c) => c.id === catId)?.name ?? '—'

  const baseUrl = (url: string) => {
    try {
      return new URL(url).hostname
    } catch {
      return url
    }
  }

  return (
    <div>
      <Button
        label={navEnabled ? 'Disable Best Practices' : 'Enable Best Practices'}
        backgroundColor={navEnabled ? colors.orange : colors.blue200}
        onClick={onToggleNav}
      />

      <YSpacing height="10px" />
      <h3>Categories</h3>
      <YSpacing height="8px" />
      <ButtonsBar>
        <button onClick={() => setEditingCat({})}>
          <BiPencil /> New Category
        </button>
      </ButtonsBar>
      <Panel width="100%" maxWidth="500px">
        <Table headings={['Name', 'Active', '']}>
          {categories.map((cat) => (
            <tr key={cat.id}>
              <td>{cat.name}</td>
              <td>{cat.active ? 'Yes' : 'No'}</td>
              <td>
                <FlexContainer>
                  <BiTrash onClick={() => onDeleteCategory(cat.id)} />
                  <XSpacing width="10px" />
                  <BiPencil onClick={() => setEditingCat(cat)} />
                </FlexContainer>
              </td>
            </tr>
          ))}
        </Table>
      </Panel>

      <YSpacing height="20px" />
      <h3>Documents</h3>
      <YSpacing height="8px" />
      <ButtonsBar>
        <button onClick={() => setEditingDoc({})}>
          <BiPencil /> New Document
        </button>
      </ButtonsBar>
      <Panel width="100%" maxWidth="700px">
        <Table headings={['Title', 'URL', 'Category', 'Visibility', '']}>
          {docs.map((doc) => (
            <tr key={doc.id}>
              <td>{doc.title}</td>
              <td>
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noreferrer"
                  title={doc.url}
                >
                  {baseUrl(doc.url)}
                </a>
              </td>
              <td>{categoryName(doc.categoryId)}</td>
              <td>
                {doc.allHqs ? (
                  'All HQs'
                ) : doc.enabledHqs?.length ? (
                  <HqVisibilityCell
                    enabledHqs={doc.enabledHqs}
                    userHeadquarters={userHeadquarters}
                  />
                ) : (
                  '—'
                )}
              </td>
              <td>
                <FlexContainer>
                  <BiTrash onClick={() => onDeleteDoc(doc.id)} />
                  <XSpacing width="10px" />
                  <BiPencil onClick={() => setEditingDoc(doc)} />
                </FlexContainer>
              </td>
            </tr>
          ))}
        </Table>
      </Panel>

      {editingCat !== false && (
        <BestPracticeCategoryModal
          category={editingCat}
          onSave={onSaveCategory}
          isSavingCategory={isLoadingSaveCategory}
          onClose={() => setEditingCat(false)}
        />
      )}

      {editingDoc !== false && (
        <BestPracticeDocModal
          doc={editingDoc}
          categories={categories}
          isSavingDoc={isLoadingSaveDoc}
          availableHqs={userHeadquarters}
          onSave={onSaveDoc}
          onClose={() => setEditingDoc(false)}
        />
      )}
    </div>
  )
}

const HqVisibilityCell: React.FC<{
  enabledHqs: number[]
  userHeadquarters: IndexHeadquarter[]
}> = ({ enabledHqs, userHeadquarters }) => {
  const [show, setShow] = useState(false)
  const hqs = userHeadquarters.filter((hq) => enabledHqs.includes(hq.id))

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <BiShow
        size={18}
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      />
      {show && hqs.length > 0 && (
        <div
          style={{
            position: 'absolute',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#fff',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '10px 14px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            whiteSpace: 'nowrap',
            zIndex: 10,
            fontSize: '13px',
            lineHeight: '1.6',
          }}
        >
          {hqs.map((hq) => (
            <div key={hq.id}>
              <strong>{hq.prefix}</strong> — {hq.name}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default BestPracticesListGroup
